import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import { Box, Checkbox, CircularProgress, FormControlLabel, Grid2, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { DL38299Form as DL38299FormType, getDL38299FormSchema } from "../../../types/forms/capture";
import { DL38299 } from "../../../types/model/dl38299";

interface DL38299CheckboxProps {
  name: string;
  label: string;
  isEditing: boolean;
  isSaving: boolean;
  sx?: object;
  canEdit: boolean;
}

export function DL38299Checkbox({ name, label, isEditing, isSaving, sx, canEdit }: Readonly<DL38299CheckboxProps>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={<Checkbox {...field} checked={field.value} />}
          label={label}
          disabled={!isEditing || isSaving || !canEdit}
          sx={{
            "& .MuiFormControlLabel-label": {
              color: "text.primary",
              opacity: 1,
            },
            "&.Mui-disabled .MuiFormControlLabel-label": {
              color: "text.primary",
            },
            ...sx,
          }}
        />
      )}
    />
  );
}

interface DL38299FormProps {
  DL38299Data?: DL38299 | null;
  canEdit: boolean;
  onSubmit: (values: DL38299FormType) => Promise<void>;
}

export default function DL38299Form({
  DL38299Data,
  canEdit,
  onSubmit,
}: Readonly<DL38299FormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isUpdateForm, setIsUpdateForm] = useState<boolean>(!!DL38299Data);
  const [isEditing, setIsEditing] = useState<boolean>(!isUpdateForm);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getDL38299FormSchema(t);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      imea: DL38299Data?.imea ?? false,
      inta1: DL38299Data?.inta1 ?? false,
      intb1: DL38299Data?.intb1 ?? false,
      intc1: DL38299Data?.intc1 ?? false,
      intd1: DL38299Data?.intd1 ?? false,
      inte1: DL38299Data?.inte1 ?? false,
      intf1: DL38299Data?.intf1 ?? false,
      intg1: DL38299Data?.intg1 ?? false,
      inth: DL38299Data?.inth ?? false,
      inti: DL38299Data?.inti ?? false,
      intj: DL38299Data?.intj ?? false,
      intl: DL38299Data?.intl ?? false,
      intm: DL38299Data?.intm ?? false,
      intn: DL38299Data?.intn ?? false,
      into: DL38299Data?.into ?? false,
      intp: DL38299Data?.intp ?? false,
      intq: DL38299Data?.intq ?? false,
  
      inta2: DL38299Data?.inta2 ?? false,
      intb2: DL38299Data?.intb2 ?? false,
      intc2: DL38299Data?.intc2 ?? false,
      intd2: DL38299Data?.intd2 ?? false,
      inte2: DL38299Data?.inte2 ?? false,
      intf2: DL38299Data?.intf2 ?? false,
      intg2: DL38299Data?.intg2 ?? false,
  
      alra1: DL38299Data?.alra1 ?? false,
      alrb1: DL38299Data?.alrb1 ?? false,
      alrc1: DL38299Data?.alrc1 ?? false,
      alrd1: DL38299Data?.alrd1 ?? false,
      alre1: DL38299Data?.alre1 ?? false,
      alrf: DL38299Data?.alrf ?? false,
      alrg: DL38299Data?.alrg ?? false,
      alrh: DL38299Data?.alrh ?? false,
      alri: DL38299Data?.alri ?? false,
      alrj: DL38299Data?.alrj ?? false,
      alrl: DL38299Data?.alrl ?? false,
  
      alra2: DL38299Data?.alra2 ?? false,
      alrb2: DL38299Data?.alrb2 ?? false,
      alrc2: DL38299Data?.alrc2 ?? false,
      alrd2: DL38299Data?.alrd2 ?? false,
      alre2: DL38299Data?.alre2 ?? false,
    },
  });

  const handleFormSubmit = async (values: DL38299FormType) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      if (isUpdateForm) setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    } 
  };

  // Sync form state when DL38299Data changes
  useEffect(() => {
    setIsUpdateForm(!!DL38299Data);
    if (DL38299Data) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [DL38299Data]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography variant="h5">
              {t("captures.capture.dl38299.title")}
            </Typography>

            {/* Action Icons */}
            {isEditing && canEdit ? ( 
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title={t("generic.save")}>
                  <IconButton
                    color="success"
                    onClick={methods.handleSubmit(handleFormSubmit)}
                    disabled={isSaving}
                  >
                    {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("generic.cancel")}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setIsEditing(false);
                      methods.reset();
                    }}
                  >
                    <EditOffIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              canEdit && (
                <Tooltip title={t("generic.edit")}>
                  <IconButton
                    color="primary"
                    onClick={() => setIsEditing(true)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Box>
        </Grid2>
        
        {/*ZONA DE PROTEÇÃO IMEDIATA*/}
        <Typography variant="h5" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
          {t("captures.capture.dl38299.immediate.title")}
        </Typography>

        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
            {t("captures.capture.dl38299.restricted")}
          </Typography>

          <DL38299Checkbox
            name="imea"
            label={t("captures.capture.dl38299.immediate.restricted.imea")}
            isEditing={isEditing}
            isSaving={isSaving}
            sx={formStyles}
            canEdit={canEdit}
          />
        </Paper>

        <Grid2 container spacing={2}>
          {/*ZONA DE PROTEÇÃO INTERMÉDIA*/}
          <Typography variant="h5" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            {t("captures.capture.dl38299.intermediate.title")}
          </Typography>

          <Grid2 container spacing={2}>
            {/*Interditas ou Condicionadas*/}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("captures.capture.dl38299.restricted")}
                </Typography>
                <Grid2 container spacing={0.2}>
                  {[
                    "inta1","intb1","intc1","intd1","inte1","intf1","intg1",
                    "inth","inti","intj","intl","intm","intn","into","intp","intq"
                  ].map((field) => (
                    <Grid2 key={field} size={12}>
                      <DL38299Checkbox
                        name={field}
                        label={t(`captures.capture.dl38299.intermediate.restricted.${field}`)}
                        isEditing={isEditing}
                        isSaving={isSaving}
                        sx={formStyles}
                        canEdit={canEdit}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Paper>
            </Grid2>

            {/*Interditas*/}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("captures.capture.dl38299.forbidden")}
                </Typography>
                <Grid2 container spacing={0.2}>
                  {["inta2","intb2","intc2","intd2","inte2","intf2","intg2"].map((field) => (
                    <Grid2 key={field} size={12}>
                      <DL38299Checkbox
                        name={field}
                        label={t(`captures.capture.dl38299.intermediate.forbidden.${field}`)}
                        isEditing={isEditing}
                        isSaving={isSaving}
                        sx={formStyles}
                        canEdit={canEdit}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Paper>
            </Grid2>
          </Grid2>

          {/*ZONA DE PROTEÇÃO ALARGADA*/}
          <Typography variant="h5" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            {t("captures.capture.dl38299.extended.title")}
          </Typography>

          <Grid2 container spacing={2}>
            {/*Interditas ou Condicionadas*/}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("captures.capture.dl38299.restricted")}
                </Typography>
                <Grid2 container spacing={0.2}>
                  {[
                    "alra1","alrb1","alrc1","alrd1","alre1",
                    "alrf","alrg","alrh","alri","alrj","alrl"
                  ].map((field) => (
                    <Grid2 key={field} size={12}>
                      <DL38299Checkbox
                        name={field}
                        label={t(`captures.capture.dl38299.extended.restricted.${field}`)}
                        isEditing={isEditing}
                        isSaving={isSaving}
                        sx={formStyles}
                        canEdit={canEdit}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Paper>
            </Grid2>

            {/*Interditas*/}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("captures.capture.dl38299.forbidden")}
                </Typography>
                <Grid2 container spacing={0.2}>
                  {["alra2","alrb2","alrc2","alrd2","alre2"].map((field) => (
                    <Grid2 key={field} size={12}>
                      <DL38299Checkbox
                        name={field}
                        label={t(`captures.capture.dl38299.extended.forbidden.${field}`)}
                        isEditing={isEditing}
                        isSaving={isSaving}
                        sx={formStyles}
                        canEdit={canEdit}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Paper>
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}